import type { ReactNode } from 'react';
import { PanelLeft } from 'lucide-react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { Select, SideRail, TopBar } from '@vizial-ui/components';

import { useSidebarStore } from '../../store/sidebarStore';
import saIconWhite from './images/sa-icon-white.png';
import styles from './AppShell.module.css';
import { NAV_ITEMS } from './utils/navItems';
import { TENANT_OPTIONS } from './utils/tenantOptions';

export interface AppShellProps {
  children: ReactNode;
}

function handleNavigation(
  key: string,
  navigate: ReturnType<typeof useNavigate>,
) {
  const item = NAV_ITEMS.find((navItem) => navItem.key === key);
  if (item?.path) {
    navigate({ to: item.path });
  }
}

export function AppShell({ children }: AppShellProps) {
  const collapsed = useSidebarStore((state) => state.collapsed);
  const toggle = useSidebarStore((state) => state.toggle);
  const navigate = useNavigate();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });

  const activeItem = NAV_ITEMS.find((item) => item.path === pathname);

  return (
    <div className={styles.shell}>
      <div className={styles.railColumn}>
        <SideRail
          items={NAV_ITEMS}
          activeKey={activeItem?.key ?? ''}
          onNavigate={(key) => handleNavigation(key, navigate)}
          collapsed={collapsed}
          onCollapse={toggle}
          logo={
            <Link to="/" className={styles.logo}>
              <img
                src={saIconWhite}
                alt="Secure Agility"
                className={styles.logoMark}
              />
              {!collapsed && (
                <span className={styles.logoText}>Secure Agility</span>
              )}
            </Link>
          }
        />
        {!collapsed && (
          <div className={styles.tenantSection}>
            <span className={styles.tenantLabel}>Admin – Viewing as</span>
            <Select
              size="sm"
              options={TENANT_OPTIONS}
              defaultValue="security-customer-a"
              aria-label="Tenant selector"
            />
          </div>
        )}
      </div>

      <div className={styles.mainColumn}>
        <div className={styles.topBarRow}>
          <button
            type="button"
            className={styles.collapseButton}
            onClick={toggle}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <PanelLeft size={18} />
          </button>
          <div className={styles.topBarSlot}>
            <TopBar
              title="Clara"
              user={{ name: 'John Doe', role: '' }}

              actions={<span className={styles.previewBadge}>Preview</span>}
            />
          </div>
        </div>
        <main className={styles.content}>{children}</main>
      </div>
    </div>
  );
}
